import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ThemePreference } from 'shared';

interface ThemeState {
  theme: ThemePreference;
  resolvedTheme: 'light' | 'dark';

  // Actions
  setTheme: (theme: ThemePreference) => void;
}

function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(theme: ThemePreference): 'light' | 'dark' {
  const resolvedTheme = theme === 'system' ? getSystemTheme() : theme;

  if (typeof document !== 'undefined') {
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(resolvedTheme);
  }

  return resolvedTheme;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      theme: 'light',
      resolvedTheme: applyTheme('light'),

      setTheme: (theme: ThemePreference) => {
        const resolvedTheme = applyTheme(theme);
        set({ theme, resolvedTheme });
      },
    }),
    {
      name: 'theme-storage',
      partialize: (state) => ({
        theme: state.theme,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Always force light theme for the backoffice
          applyTheme('light');
        }
      },
    }
  )
);

// Listen for system theme changes
if (typeof window !== 'undefined') {
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  mediaQuery.addEventListener('change', () => {
    const state = useThemeStore.getState();
    if (state.theme === 'system') {
      state.setTheme('system');
    }
  });
}
